import React from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { UseCases } from "@planzone/core";
import { VisibilityWatcher } from "@common/visibilityWatcher";
import { Calendar } from "@common/calendar";
import { LayoutWatcher } from "@common/layoutWatcher";
import { ScreenWatcher } from "@common/screenWatcher";
import {
  useEditEvent,
  useListEdition,
  useEditTask,
  useMilestoneEdition,
} from "@planzone/view-models";
import { usePermissions } from "@scenes/safeguard";
import {
  task as taskMock,
  list as listMock,
  OptimizedGantt,
  task,
  milestone,
  event,
} from "@planzone/dto";

import {
  isDone,
  isEvent,
  isList,
  isMilestone,
  isProject,
  isTask,
} from "@scenes/workloads/graph/elementsOverlay/element/utils";
import { reCalculateDateRange } from "@scenes/workloads/utils";

import { Props } from "./types";
import styles from "./styles.scss";

// Same shape as useElementEdition in graph/elementsOverlay/element/utils/edit.ts.
// Round Y/M/D/H/M in user TZ to UTC ISO, dropping seconds/ms. The two hooks
// are duplicates — kept in lockstep until the de-dup follow-up.
function toEventTimeIso(iso: string, userTimezone: string): string {
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone(userTimezone);
  return (
    DateTime.fromObject(
      {
        year: dt.year,
        month: dt.month,
        day: dt.day,
        hour: dt.hour,
        minute: dt.minute,
      },
      { zone: userTimezone },
    )
      .toUTC()
      .toISO() ?? ""
  );
}

function useEditElement(element: OptimizedGantt[number]) {
  const userTimezone = DateTime.local().zoneName;
  const { isRequestPending: isTaskEditionRequestPending, editTask } =
    useEditTask({ ...task, ...element });
  const { isRequestPending: isListEditionRequestPending, editList } =
    useListEdition();
  const { isRequestPending: isMilestoneEditionRequestPending, editMilestone } =
    useMilestoneEdition({ ...milestone, ...element });
  const { isRequestPending: isEventEditionRequestPending, editEvent } =
    useEditEvent({ ...event, ...element });

  return {
    isEditionRequestPending: isTask(element)
      ? isTaskEditionRequestPending
      : isList(element)
        ? isListEditionRequestPending
        : isMilestone(element)
          ? isMilestoneEditionRequestPending
          : isEvent(element)
            ? isEventEditionRequestPending
            : false,
    editElement: (nextElement: OptimizedGantt[number]) => {
      return isTask(element)
        ? !isTaskEditionRequestPending &&
            editTask({
              nextTask: {
                ...taskMock,
                ...element,
                activityKeyRank: nextElement.keyRank,
                startDate: nextElement.startDate,
                endDate: nextElement.endDate,
              },
              task: {
                ...taskMock,
                ...element,
                activityKeyRank: element.keyRank,
                // percentComplete: element.progress,
              },
            })
        : isMilestone(element)
          ? !isMilestoneEditionRequestPending &&
            editMilestone({
              nextMilestone: {
                ...milestone,
                ...element,
                keyRank: nextElement.keyRank,
                endDate: nextElement.endDate,
              },
              milestone: { ...milestone, ...element },
            })
          : isEvent(element)
            ? !isEventEditionRequestPending &&
              editEvent({
                nextEvent: {
                  ...event,
                  ...element,
                  startTime: toEventTimeIso(
                    nextElement.startDate,
                    userTimezone,
                  ),
                  endTime: toEventTimeIso(nextElement.endDate, userTimezone),
                },
                event: { ...event, ...element },
              })
            : isList(element)
              ? !isListEditionRequestPending &&
                editList({
                  list: { ...listMock, ...element },
                  nextList: {
                    ...listMock,
                    ...element,
                    startDate: nextElement.startDate,
                    endDate: nextElement.endDate,
                  },
                })
              : isProject(element) &&
                !isListEditionRequestPending &&
                editList({
                  list: {
                    ...listMock,
                    ...element,
                    uuid: element.activityRootUuid,
                  },
                  nextList: {
                    ...listMock,
                    ...element,
                    startDate: nextElement.startDate,
                    endDate: nextElement.endDate,
                  },
                });
    },
  };
}

function Dates({ element, minDate, maxDate }: Props) {
  const { isEditionRequestPending, editElement } = useEditElement(element);
  const userTimezone = DateTime.local().zoneName;
  const min = DateTime.fromISO(minDate, { zone: "utc" });
  const max = DateTime.fromISO(maxDate, { zone: "utc" });
  const permissions = usePermissions([
    UseCases.editProject,
    UseCases.editList,
    UseCases.editTask,
    UseCases.editMilestone,
    UseCases.editEvent,
  ]);
  const canEdit = isProject(element)
    ? permissions.canEditProject
    : isList(element)
      ? permissions.canEditList
      : isTask(element)
        ? permissions.canEditTask
        : isMilestone(element)
          ? permissions.canEditMilestone
          : isEvent(element)
            ? permissions.canEditEvent
            : false;
  const recalculated = reCalculateDateRange(element, min, max);
  const canOpenCalendar = !isDone(element) && canEdit;

  return (
    <div
      className={classNames(styles.details, {
        [styles.list]: isList(element),
        [styles.project]: isProject(element),
      })}
    >
      {isMilestone(element) ? (
        <div className={styles.block}>
          <p className={styles.text}>-</p>
        </div>
      ) : (
        <VisibilityWatcher>
          {(isVisible, show, hide) => (
            <ScreenWatcher
              renderChildren={({ height: screenHeight }) => (
                <LayoutWatcher
                  renderChildren={({ top, height }) => (
                    <div
                      className={classNames(styles.block, {
                        [styles.closedCalendar]: !canOpenCalendar,
                      })}
                    >
                      <p
                        className={classNames(styles.text, {
                          [styles.recalculated]:
                            !!recalculated.startDate && !element.startDate,
                        })}
                        onClick={() =>
                          canOpenCalendar && (isVisible ? hide() : show())
                        }
                      >
                        {recalculated.startDate
                          ? !!element.startDate
                            ? recalculated.startDate.toLocaleString(
                                DateTime.DATE_SHORT,
                              )
                            : `(${recalculated.startDate.toLocaleString(DateTime.DATE_SHORT)})`
                          : "-"}
                      </p>
                      {isVisible && canOpenCalendar && (
                        <div
                          className={styles.calendar}
                          style={{
                            bottom:
                              top + height + 200 > screenHeight
                                ? `calc(100% + 1rem)`
                                : undefined,
                            top:
                              top + height + 200 > screenHeight
                                ? "initial"
                                : `calc(100% + 1rem)`,
                          }}
                        >
                          <Calendar
                            date={{
                              year: recalculated.startDate.year,
                              month: recalculated.startDate.month - 1,
                              day: recalculated.startDate.day,
                            }}
                            minDate={{
                              year: min.year,
                              month: min.month - 1,
                              day: min.day,
                            }}
                            editDate={(date) => {
                              !isEditionRequestPending &&
                                editElement({
                                  ...element,
                                  startDate:
                                    DateTime.fromObject(
                                      {
                                        year: date.year,
                                        month: date.month + 1,
                                        day: date.day,
                                      },
                                      { zone: userTimezone },
                                    )
                                      .toUTC()
                                      .toISO() ?? "",
                                });
                              hide();
                            }}
                            clear={
                              isProject(element)
                                ? undefined
                                : () => {
                                    !isEditionRequestPending &&
                                      editElement({
                                        ...element,
                                        startDate: "",
                                      });
                                    hide();
                                  }
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                />
              )}
            />
          )}
        </VisibilityWatcher>
      )}
      <VisibilityWatcher>
        {(isVisible, show, hide) => (
          <ScreenWatcher
            renderChildren={({ height: screenHeight }) => (
              <LayoutWatcher
                renderChildren={({ top, height }) => (
                  <div
                    className={classNames(styles.block, {
                      [styles.closedCalendar]: !canOpenCalendar,
                    })}
                  >
                    <p
                      className={classNames(styles.text, {
                        [styles.recalculated]:
                          !!recalculated.startDate && !element.endDate,
                      })}
                      onClick={() =>
                        canOpenCalendar && (isVisible ? hide() : show())
                      }
                    >
                      {recalculated.endDate
                        ? !!element.endDate
                          ? recalculated.endDate.toLocaleString(
                              DateTime.DATE_SHORT,
                            )
                          : `(${recalculated.endDate.toLocaleString(DateTime.DATE_SHORT)})`
                        : "-"}
                    </p>
                    {isVisible && canOpenCalendar && (
                      <div
                        className={styles.calendar}
                        style={{
                          bottom:
                            top + height + 200 > screenHeight
                              ? `calc(100% + 1rem)`
                              : undefined,
                          top:
                            top + height + 200 > screenHeight
                              ? "initial"
                              : `calc(100% + 1rem)`,
                        }}
                      >
                        <Calendar
                          date={{
                            year: recalculated.endDate.year,
                            month: recalculated.endDate.month - 1,
                            day: recalculated.endDate.day,
                          }}
                          minDate={{
                            year: min.year,
                            month: min.month - 1,
                            day: min.day,
                          }}
                          editDate={(date) => {
                            !isEditionRequestPending &&
                              editElement({
                                ...element,
                                endDate:
                                  DateTime.fromObject(
                                    {
                                      year: date.year,
                                      month: date.month + 1,
                                      day: date.day,
                                    },
                                    { zone: userTimezone },
                                  )
                                    .toUTC()
                                    .toISO() ?? "",
                              });
                            hide();
                          }}
                          clear={
                            isProject(element)
                              ? undefined
                              : () => {
                                  !isEditionRequestPending &&
                                    editElement({
                                      ...element,
                                      endDate: "",
                                    });
                                  hide();
                                }
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              />
            )}
          />
        )}
      </VisibilityWatcher>
      <div
        className={classNames(styles.block, styles.readOnly, {
          [styles.closedCalendar]: !canOpenCalendar,
        })}
      >
        <p className={styles.text}>
          {/*{element.duration ? element.duration : "-"}*/}
          {"-"}
        </p>
      </div>
    </div>
  );
}

export { Dates };
